import { API_BASE } from "@/lib/apiClient";
import type { WebsiteBlocks, WebsiteContentDocument, WebsiteTile, WebsiteTileCopy } from "@/lib/websiteContentApi";
import { publicWebsiteOrigin } from "@/lib/websiteContentCatalog";
import {
  TILE_SECTIONS,
  blankTile,
  linkChoices,
  seedSection,
  type TileSectionId,
} from "@/lib/websiteTiles";

type Locale = "de" | "en";

type WebsiteTilesEditorProps = {
  blocks: WebsiteBlocks;
  document: WebsiteContentDocument;
  locale: Locale;
  canEdit: boolean;
  hostname: string;
  onChange: (blocks: WebsiteBlocks) => void;
  onUpload: (file: File) => Promise<string>;
};

function previewSrc(src: string, hostname: string): string {
  if (!src) return "";
  if (src.startsWith("/api/")) return `${API_BASE}${src}`;
  if (src.startsWith("/images/")) return `${publicWebsiteOrigin(hostname)}${src}`;
  return src;
}

export function WebsiteTilesEditor({
  blocks,
  document,
  locale,
  canEdit,
  hostname,
  onChange,
  onUpload,
}: WebsiteTilesEditorProps) {
  function tilesOf(section: TileSectionId): WebsiteTile[] {
    return blocks[section] ?? seedSection(section, document);
  }

  function write(section: TileSectionId, items: WebsiteTile[]) {
    onChange({ ...blocks, [section]: items });
  }

  function patch(section: TileSectionId, index: number, patchTile: Partial<WebsiteTile>) {
    const items = tilesOf(section).map((tile, tileIndex) => (tileIndex === index ? { ...tile, ...patchTile } : tile));
    write(section, items);
  }

  function patchCopy(section: TileSectionId, index: number, patchCopyFields: Partial<WebsiteTileCopy>) {
    const current = tilesOf(section)[index];
    if (!current) return;
    patch(section, index, { [locale]: { ...current[locale], ...patchCopyFields } });
  }

  function move(section: TileSectionId, index: number, direction: -1 | 1) {
    const items = [...tilesOf(section)];
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const [item] = items.splice(index, 1);
    if (!item) return;
    items.splice(target, 0, item);
    write(section, items);
  }

  function remove(section: TileSectionId, index: number) {
    write(section, tilesOf(section).filter((_, tileIndex) => tileIndex !== index));
  }

  function restore(section: TileSectionId) {
    const next = { ...blocks };
    delete next[section];
    onChange(next);
  }

  return (
    <div className="space-y-4">
      {TILE_SECTIONS.map((section) => {
        const items = tilesOf(section.id);
        const custom = Array.isArray(blocks[section.id]);
        return (
          <details key={section.id} open={section.id === "stories" || section.id === "highlights"} className="rounded-2xl border border-separator bg-card">
            <summary className="cursor-pointer px-5 py-4 text-base font-semibold text-label">
              {section.label}
              <span className="ml-2 text-sm font-normal text-label-secondary">{items.length}</span>
            </summary>
            <div className="space-y-4 border-t border-separator px-5 py-4">
              <p className="text-sm text-label-secondary">{section.hint}</p>
              {items.length === 0 ? (
                <p className="text-sm text-label-secondary">Dieser Bereich ist leer und erscheint nicht auf der Website.</p>
              ) : null}
              <div className="space-y-4">
                {items.map((tile, index) => {
                  const copy = tile[locale];
                  return (
                    <article key={tile.id} className="grid gap-4 rounded-2xl border border-separator p-3 sm:grid-cols-[220px_minmax(0,1fr)]">
                      <div>
                        {tile.image ? (
                          <img src={previewSrc(tile.image, hostname)} alt="" className="aspect-[4/3] w-full rounded-xl object-cover" />
                        ) : (
                          <div className="flex aspect-[4/3] items-center justify-center rounded-xl bg-fill-tertiary text-sm text-label-secondary">
                            Noch kein Foto
                          </div>
                        )}
                        <label className="mt-2 block text-sm font-medium text-label">
                          Foto wählen
                          <input
                            className="mt-1 block w-full text-sm"
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            disabled={!canEdit}
                            onChange={(event) => {
                              const file = event.target.files?.[0];
                              event.target.value = "";
                              if (!file) return;
                              void onUpload(file)
                                .then((url) => patch(section.id, index, { image: url }))
                                .catch(() => undefined);
                            }}
                          />
                        </label>
                      </div>
                      <div className="space-y-3">
                        <div className="flex flex-wrap gap-2">
                          <button type="button" className="rounded-lg border border-separator px-2 py-1 text-sm" disabled={!canEdit || index === 0} onClick={() => move(section.id, index, -1)}>
                            Nach oben
                          </button>
                          <button type="button" className="rounded-lg border border-separator px-2 py-1 text-sm" disabled={!canEdit || index === items.length - 1} onClick={() => move(section.id, index, 1)}>
                            Nach unten
                          </button>
                          <button type="button" className="rounded-lg border border-separator px-2 py-1 text-sm" disabled={!canEdit} onClick={() => remove(section.id, index)}>
                            Entfernen
                          </button>
                          <label className="ml-auto flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={tile.visible}
                              disabled={!canEdit}
                              onChange={(event) => patch(section.id, index, { visible: event.target.checked })}
                            />
                            Auf der Website zeigen
                          </label>
                        </div>
                        {section.id === "stories" || section.id === "facilities" ? (
                          <Field label={section.id === "facilities" ? "Kategorie" : "Kleine Zeile"} value={section.id === "facilities" ? copy.badge : copy.eyebrow} disabled={!canEdit} onChange={(value) => patchCopy(section.id, index, section.id === "facilities" ? { badge: value } : { eyebrow: value })} />
                        ) : null}
                        <Field label={section.id === "gallery" ? "Bildtext" : "Überschrift"} value={copy.title} disabled={!canEdit} onChange={(value) => patchCopy(section.id, index, { title: value })} />
                        {section.id !== "gallery" && section.id !== "links" ? (
                          <Field label="Kurzer Text" value={copy.text} multiline disabled={!canEdit} onChange={(value) => patchCopy(section.id, index, { text: value })} />
                        ) : null}
                        {section.id === "surroundings" ? (
                          <Field label="Entfernung" value={copy.badge} disabled={!canEdit} onChange={(value) => patchCopy(section.id, index, { badge: value })} />
                        ) : null}
                        {section.id === "facilities" ? (
                          <>
                            <Field
                              label="Stichworte, eine Zeile pro Wort"
                              value={tile.highlights.join("\n")}
                              multiline
                              disabled={!canEdit}
                              onChange={(value) =>
                                patch(section.id, index, { highlights: value.split("\n") })
                              }
                            />
                            <Field label="Ausführlicher Text" value={copy.detail} multiline disabled={!canEdit} onChange={(value) => patchCopy(section.id, index, { detail: value })} />
                          </>
                        ) : null}
                        {section.id === "links" ? (
                          <label className="block text-sm">
                            <span className="font-medium text-label">Wohin führt die Kachel?</span>
                            <select
                              className="mt-1 h-10 w-full rounded-lg border border-separator bg-card px-2"
                              value={linkChoices().some((choice) => choice.value === tile.href) ? tile.href : "custom"}
                              disabled={!canEdit}
                              onChange={(event) => {
                                const value = event.target.value;
                                if (value !== "custom") patch(section.id, index, { href: value });
                              }}
                            >
                              {linkChoices().map((choice) => (
                                <option key={choice.value} value={choice.value}>
                                  {choice.label}
                                </option>
                              ))}
                              <option value="custom">Eigene Adresse</option>
                            </select>
                            {!linkChoices().some((choice) => choice.value === tile.href) ? (
                              <input
                                className="mt-2 h-10 w-full rounded-lg border border-separator bg-card px-3"
                                value={tile.href}
                                disabled={!canEdit}
                                placeholder="https://… oder #bereich"
                                onChange={(event) => patch(section.id, index, { href: event.target.value })}
                              />
                            ) : null}
                          </label>
                        ) : null}
                        {section.id !== "stories" ? (
                          <label className="block text-sm">
                            <span className="font-medium text-label">Größe</span>
                            <select
                              className="mt-1 h-10 w-full rounded-lg border border-separator bg-card px-2"
                              value={tile.size}
                              disabled={!canEdit}
                              onChange={(event) => patch(section.id, index, { size: event.target.value as WebsiteTile["size"] })}
                            >
                              <option value="normal">Normal</option>
                              <option value="wide">Breit</option>
                              <option value="large">Ganz groß</option>
                            </select>
                          </label>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-label-on-accent"
                  disabled={!canEdit}
                  onClick={() => write(section.id, [...tilesOf(section.id), blankTile(section.id)])}
                >
                  Kachel hinzufügen
                </button>
                {custom ? (
                  <button type="button" className="rounded-xl border border-separator px-4 py-2 text-sm" disabled={!canEdit} onClick={() => restore(section.id)}>
                    Standard wiederherstellen
                  </button>
                ) : (
                  <p className="self-center text-sm text-label-secondary">Noch der Standard. Eine Änderung speichert Ihre eigenen Kacheln.</p>
                )}
              </div>
            </div>
          </details>
        );
      })}
    </div>
  );
}

function Field({
  label,
  value,
  multiline = false,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  multiline?: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const className = "mt-1 w-full rounded-lg border border-separator bg-card px-3 py-2 text-sm";
  return (
    <label className="block text-sm">
      <span className="font-medium text-label">{label}</span>
      {multiline ? (
        <textarea className={className} rows={4} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
      ) : (
        <input className={`${className} h-10`} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
      )}
    </label>
  );
}
